import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Users,
  X,
  Plus,
  Calendar,
  Shield,
  Brain,
  Send,
  Search,
  Building2,
  UserPlus,
  Mail,
  ThumbsUp,
  TrendingUp,
  MessageSquare,
  ClipboardCheck,
  MessageSquareText,
  Pencil,
  Check
} from 'lucide-react'
import { integrationService, EmployeeData } from '@/services/api/integrationService'
import questionBankService, { FeedbackTemplate } from '@/services/api/questionBankService'
import type { FeedbackQuestion } from '@/services/api/feedbackService'
import toast from 'react-hot-toast'
import { EmployeePicker } from './EmployeePicker'
import { sastToday } from '@/components/admin/lifecycle/spellCoverage'
import {
  answerTypeLabel,
  customQuestion,
  defaultQuestions,
  questionSections,
  rewordQuestion,
  snapshotFromTemplate,
} from './questionSnapshot'

interface InternalStakeholder {
  id: string
  name: string
  role: string
  department: string
  campus?: string
  email?: string
}

interface ExternalStakeholder {
  id: string
  name: string
  email: string
  organization: string
  category: 'PEER' | 'CLIENT' | 'PARTNER' | 'OTHER'
}

interface FeedbackRequestDialogProps {
  open: boolean
  onClose?: () => void
  onOpenChange?: (open: boolean) => void  // Alternative to onClose
  requestType: 'MATRIX' | 'EMPLOYEE_REQUESTED' | 'UPWARD_360' | 'INFORMATION_CHECKIN' | 'PROJECT'
  currentUserId: string
  onSubmit: (data: {
    requested_for_user_id: string
    request_type: string
    title: string
    question_template_id?: string  // Use question_template_id to match backend
    stakeholders: Array<{
      stakeholder_user_id?: string  // Optional - only for internal stakeholders
      stakeholder_name: string
      stakeholder_email: string
      is_internal: boolean
      category: string
    }>
    // The requester's final list, each question with its source (#5): it is
    // what stakeholders answer, in place of the template's current questions.
    custom_questions: FeedbackQuestion[]
    due_date: string
    is_confidential: boolean
    ai_analysis_enabled: boolean
  }) => Promise<any>
}

export default function FeedbackRequestDialog({
  open,
  onClose,
  onOpenChange,
  requestType,
  currentUserId,
  onSubmit
}: FeedbackRequestDialogProps) {
  // Handle close via either callback
  const handleClose = () => {
    if (onClose) onClose()
    if (onOpenChange) onOpenChange(false)
  }
  // Internal stakeholders from F&O (using EmployeePicker component)
  const [selectedInternalStakeholders, setSelectedInternalStakeholders] = useState<InternalStakeholder[]>([])
  
  // External stakeholders
  const [externalStakeholders, setExternalStakeholders] = useState<ExternalStakeholder[]>([])
  const [showAddExternal, setShowAddExternal] = useState(false)
  const [externalName, setExternalName] = useState('')
  const [externalEmail, setExternalEmail] = useState('')
  const [externalOrg, setExternalOrg] = useState('')
  const [externalCategory, setExternalCategory] = useState<'PEER' | 'CLIENT' | 'PARTNER' | 'OTHER'>('PEER')
  const [emailError, setEmailError] = useState('')
  
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([])
  const [newQuestion, setNewQuestion] = useState('')
  // The question being reworded in place, and its draft wording
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editingText, setEditingText] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [isConfidential, setIsConfidential] = useState(requestType === 'UPWARD_360')
  const isAnonymous = false // Anonymous option removed - use Confidential instead
  // #26: AI analysis is opt-in — default unchecked.
  const [enableAI, setEnableAI] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  
  // Template state
  const [availableTemplates, setAvailableTemplates] = useState<FeedbackTemplate[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<FeedbackTemplate | null>(null)
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [useTemplate, setUseTemplate] = useState(true)

  // Load templates when dialog opens
  useEffect(() => {
    if (open) {
      loadTemplates()
      
      // Set default due date to 2 weeks from today in South Africa (the UTC
      // day is still yesterday until 02:00)
      setDueDate(sastToday(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)))
      
      // Reset selections
      setSelectedInternalStakeholders([])
      setExternalStakeholders([])
      setIsConfidential(requestType === 'UPWARD_360')
      setEnableAI(false)  // #26: opt-in each time the dialog opens
      setSelectedTemplate(null)
      setUseTemplate(true)
      setEditingIndex(null)
    }
  }, [open, requestType])

  const loadTemplates = async () => {
    setLoadingTemplates(true)
    try {
      // Map request type to feedback context
      const contextMap: Record<string, string> = {
        'MATRIX': 'matrix',
        'EMPLOYEE_REQUESTED': 'employee_requested',
        'UPWARD_360': 'upward_360',
        'INFORMATION_CHECKIN': 'peer',  // Or create new context
        'PROJECT': 'project'  // For UAT and project-based feedback
      }
      
      const context = contextMap[requestType]
      if (import.meta.env.DEV) console.log(`🔍 Loading templates for context: ${context} (request type: ${requestType})`)
      
      const response = await questionBankService.listTemplates({
        feedback_context: context as any,
        is_active: true,
        limit: 50
      })
      
      if (import.meta.env.DEV) console.log(`✅ Loaded ${response.items.length} templates:`, response.items)
      setAvailableTemplates(response.items)
      
      // Auto-select default template if available
      const defaultTemplate = response.items.find(t => t.is_default)
      if (defaultTemplate) {
        if (import.meta.env.DEV) console.log('✅ Using default template:', defaultTemplate.template_name)
        setSelectedTemplate(defaultTemplate)
        loadTemplateQuestions(defaultTemplate)
      } else if (response.items.length > 0) {
        // Select first template
        if (import.meta.env.DEV) console.log('✅ Using first available template:', response.items[0].template_name)
        setSelectedTemplate(response.items[0])
        loadTemplateQuestions(response.items[0])
      } else {
        // No templates, fall back to hardcoded questions
        console.warn('⚠️ No templates found, using hardcoded questions')
        setUseTemplate(false)
        setQuestions(defaultQuestions(requestType))
      }
    } catch (error) {
      console.error('❌ Error loading templates:', error)
      toast.error('Failed to load templates, using default questions')
      setUseTemplate(false)
      setQuestions(defaultQuestions(requestType))
    } finally {
      setLoadingTemplates(false)
    }
  }

  const loadTemplateQuestions = (template: FeedbackTemplate) => {
    if (import.meta.env.DEV) console.log('📋 Loading template questions for:', template.template_code)
    setEditingIndex(null)

    // Each question keeps its type, scale or options, required flag,
    // category and question bank id.
    const fromTemplate = snapshotFromTemplate(template)
    if (fromTemplate.length === 0) {
      console.warn('⚠️ No template_questions found, using default questions')
      setQuestions(defaultQuestions(requestType))
      return
    }
    setQuestions(fromTemplate)
  }

  const handleTemplateChange = (templateId: string) => {
    const template = availableTemplates.find(t => t.id === templateId)
    if (template) {
      setSelectedTemplate(template)
      loadTemplateQuestions(template)
    }
  }

  const addExternalStakeholder = () => {
    if (!externalName.trim() || !externalEmail.trim() || !externalOrg.trim()) {
      toast.error('Please fill all external stakeholder fields')
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(externalEmail.trim())) {
      toast.error('Please enter a valid email address')
      return
    }

    // Check for duplicate email
    if (externalStakeholders.some(s => s.email.toLowerCase() === externalEmail.trim().toLowerCase())) {
      toast.error('This email address has already been added')
      return
    }

    const newExternal: ExternalStakeholder = {
      id: `external-${Date.now()}`,
      name: externalName.trim(),
      email: externalEmail.trim().toLowerCase(),
      organization: externalOrg.trim(),
      category: externalCategory
    }

    setExternalStakeholders([...externalStakeholders, newExternal])
    setExternalName('')
    setExternalEmail('')
    setExternalOrg('')
    setExternalCategory('PEER')
    setEmailError('')
    setShowAddExternal(false)
    toast.success(`Added ${newExternal.name} as external stakeholder`)
  }

  const removeExternalStakeholder = (id: string) => {
    setExternalStakeholders(externalStakeholders.filter(s => s.id !== id))
  }

  const addCustomQuestion = () => {
    if (newQuestion.trim()) {
      setQuestions([...questions, customQuestion(newQuestion, questions)])
      setNewQuestion('')
    }
  }

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index))
    setEditingIndex(null)
  }

  // A reworded question is still answered the same way (a rating stays a
  // rating). A reworded template question becomes the requester's own, with
  // no question bank id, so reports never pool it with the bank's wording.
  const startEditing = (index: number) => {
    setEditingIndex(index)
    setEditingText(questions[index].text)
  }

  const withEditApplied = (list: FeedbackQuestion[]): FeedbackQuestion[] =>
    editingIndex === null ? list : rewordQuestion(list, editingIndex, editingText)

  const saveEditing = () => {
    setQuestions(withEditApplied(questions))
    setEditingIndex(null)
  }

  const handleSubmit = async () => {
    if (import.meta.env.DEV) console.log('🔘 handleSubmit called!')
    const totalStakeholders = selectedInternalStakeholders.length + externalStakeholders.length
    if (import.meta.env.DEV) console.log('   Total stakeholders:', totalStakeholders)
    // A question still being reworded is sent as reworded
    const finalQuestions = withEditApplied(questions)
    if (import.meta.env.DEV) console.log('   Questions count:', finalQuestions.length)
    if (import.meta.env.DEV) console.log('   Due date:', dueDate)

    if (totalStakeholders === 0) {
      if (import.meta.env.DEV) console.log('❌ Validation failed: No stakeholders')
      toast.error('Please select at least one internal or external stakeholder')
      return
    }

    if (finalQuestions.length === 0) {
      if (import.meta.env.DEV) console.log('❌ Validation failed: No questions')
      toast.error('Please add at least one question')
      return
    }

    if (!dueDate) {
      if (import.meta.env.DEV) console.log('❌ Validation failed: No due date')
      toast.error('Please set a due date')
      return
    }
    
    if (import.meta.env.DEV) console.log('✅ All validations passed, proceeding to submit...')

    setSubmitting(true)
    if (import.meta.env.DEV) console.log('🚀 FeedbackRequestDialog - Starting submit...')
    console.log('   Request type:', requestType)
    if (import.meta.env.DEV) console.log('   Internal stakeholders:', selectedInternalStakeholders.length)
    console.log('   External stakeholders:', externalStakeholders.length)
    if (import.meta.env.DEV) console.log('   Questions:', finalQuestions.length)
    console.log('   Template:', selectedTemplate?.template_code)
    
    try {
      // Format stakeholders into single array
      const stakeholders = [
        // Internal stakeholders
        ...selectedInternalStakeholders.map(s => ({
          stakeholder_user_id: s.id,
          stakeholder_name: s.name,
          stakeholder_email: s.email || '',
          is_internal: true,
          category: 'peer'  // Default for internal colleagues
        })),
        // External stakeholders - use lowercase 'external' category
        ...externalStakeholders.map(s => ({
          stakeholder_name: s.name,
          stakeholder_email: s.email,
          is_internal: false,
          category: 'external'  // All external stakeholders use 'external' category
        }))
      ]

      // Generate title based on request type - use template name for PROJECT type
      const titles: Record<string, string> = {
        'MATRIX': 'Matrix Feedback Request',
        'UPWARD_360': 'Upward 360° Feedback Request',
        'EMPLOYEE_REQUESTED': 'Team Member-Requested Feedback',
        'INFORMATION_CHECKIN': 'Information Check-in',
        'PROJECT': selectedTemplate?.template_name || 'UAT / Project Feedback'
      }

      const submitData = {
        requested_for_user_id: currentUserId,
        request_type: requestType.toLowerCase(),  // Convert to lowercase
        title: titles[requestType] || 'Feedback Request',
        question_template_id: selectedTemplate?.id,  // Use question_template_id to match backend schema
        stakeholders: stakeholders,
        // Stakeholders answer exactly this list (#5): each question keeps its
        // type, scale, required flag, category and question bank id.
        custom_questions: finalQuestions,
        due_date: new Date(dueDate).toISOString(),
        is_confidential: isConfidential,
        is_anonymous: isAnonymous,
        ai_analysis_enabled: enableAI
      }
      
      if (import.meta.env.DEV) console.log('📤 Submitting feedback request:', submitData)
      const result = await onSubmit(submitData)
      if (import.meta.env.DEV) console.log('✅ Submit result:', result)
      // #27: confirm with the recipient name(s). Parent handlers no longer
      // raise their own generic toast, so this is the single source of truth.
      const recipientNames = stakeholders.map((s) => s.stakeholder_name).filter(Boolean)
      const recipientLabel =
        recipientNames.length === 0
          ? 'your stakeholders'
          : recipientNames.length === 1
          ? recipientNames[0]
          : `${recipientNames[0]} and ${recipientNames.length - 1} other${recipientNames.length - 1 === 1 ? '' : 's'}`
      toast.success(`Request sent to ${recipientLabel}`)
      handleClose()
    } catch (error) {
      console.error('Submit error:', error)
    } finally {
      setSubmitting(false)
    }
  }

  const getRequestTypeLabel = () => {
    switch (requestType) {
      case 'MATRIX': return 'Matrix Feedback Request'
      case 'EMPLOYEE_REQUESTED': return 'Team Member-Requested Feedback'
      case 'UPWARD_360': return 'Upward 360° Feedback'
      case 'INFORMATION_CHECKIN': return 'Information Check-in'
      case 'PROJECT': return 'UAT / Project Feedback'
      default: return 'Feedback Request'
    }
  }

  const noStakeholders = selectedInternalStakeholders.length === 0 && externalStakeholders.length === 0

  // #cohesion: a single heritage-blue badge for every request type — the
  // per-type icon (below) does the differentiating, so we drop the old
  // blue/green/purple/teal/amber rainbow.
  const requestTypeBadgeClass = 'bg-heritage-blue/10 text-heritage-blue border border-heritage-blue/20'

  const getRequestTypeIcon = () => {
    switch (requestType) {
      case 'MATRIX': return Users
      case 'EMPLOYEE_REQUESTED': return ThumbsUp
      case 'UPWARD_360': return TrendingUp
      case 'INFORMATION_CHECKIN': return MessageSquare
      case 'PROJECT': return ClipboardCheck
      default: return MessageSquareText
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge className={requestTypeBadgeClass}>
              {(() => {
                const TypeIcon = getRequestTypeIcon()
                return <TypeIcon className="h-3.5 w-3.5 mr-1.5" />
              })()}
              {getRequestTypeLabel()}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Stakeholder Selection with Tabs */}
          <div className="space-y-3">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" />
              Select Stakeholder(s) ({selectedInternalStakeholders.length + externalStakeholders.length} selected)
            </Label>
            <p className="text-sm text-gray-600">
              Search for internal team members from F&O or add external stakeholders
            </p>

            <Tabs defaultValue="internal" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="internal">
                  <Building2 className="h-4 w-4 mr-2" />
                  Internal ({selectedInternalStakeholders.length})
                </TabsTrigger>
                <TabsTrigger value="external">
                  <UserPlus className="h-4 w-4 mr-2" />
                  External ({externalStakeholders.length})
                </TabsTrigger>
              </TabsList>

              {/* Internal Stakeholders Tab - Smart Search */}
              <TabsContent value="internal" className="space-y-3">
                <EmployeePicker
                  selectedEmployees={selectedInternalStakeholders}
                  onSelectionChange={setSelectedInternalStakeholders}
                  placeholder="Type to search team members by name, role, or department..."
                  excludeUserId={currentUserId}
                />
              </TabsContent>

              {/* External Stakeholders Tab */}
              <TabsContent value="external" className="space-y-3">
                {/* Selected External Stakeholders */}
                {externalStakeholders.length > 0 && (
                  <div className="space-y-2 border rounded-lg p-3 bg-gray-50 max-h-40 overflow-y-auto">
                    {externalStakeholders.map((stakeholder) => (
                      <div key={stakeholder.id} className="flex items-start justify-between bg-white p-2 rounded border">
                        <div className="flex-1">
                          <p className="font-medium text-sm">{stakeholder.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-600 mt-0.5">
                            <Mail className="h-3 w-3" />
                            {stakeholder.email}
                          </div>
                          <p className="text-xs text-gray-500">{stakeholder.organization} • {stakeholder.category}</p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExternalStakeholder(stakeholder.id)}
                          className="h-6 w-6 p-0 hover:bg-red-50"
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add External Stakeholder Form */}
                {!showAddExternal ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddExternal(true)}
                    className="w-full"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Add External Stakeholder
                  </Button>
                ) : (
                  <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Name *</Label>
                        <Input
                          placeholder="Full name"
                          value={externalName}
                          onChange={(e) => setExternalName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Email *</Label>
                        <Input
                          type="email"
                          placeholder="email@example.com"
                          value={externalEmail}
                          onChange={(e) => {
                            setExternalEmail(e.target.value)
                            setEmailError('') // Clear error on change
                          }}
                          onBlur={() => {
                            if (externalEmail.trim()) {
                              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                              if (!emailRegex.test(externalEmail.trim())) {
                                setEmailError('Invalid email format')
                              } else if (externalStakeholders.some(s => s.email.toLowerCase() === externalEmail.trim().toLowerCase())) {
                                setEmailError('Email already added')
                              }
                            }
                          }}
                          className={emailError ? 'border-red-500' : ''}
                        />
                        {emailError && <p className="text-xs text-red-500 mt-1">{emailError}</p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Organisation *</Label>
                        <Input
                          placeholder="Company/Institution"
                          value={externalOrg}
                          onChange={(e) => setExternalOrg(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Category</Label>
                        <select
                          value={externalCategory}
                          onChange={(e) => setExternalCategory(e.target.value as any)}
                          className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm"
                        >
                          <option value="PEER">Peer/Colleague</option>
                          <option value="CLIENT">Client/Student</option>
                          <option value="PARTNER">Partner/Vendor</option>
                          <option value="OTHER">Other</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        onClick={addExternalStakeholder}
                        size="sm"
                        className="flex-1"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowAddExternal(false)
                          setExternalName('')
                          setExternalEmail('')
                          setExternalOrg('')
                          setEmailError('')
                        }}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Template Selection */}
          <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            {loadingTemplates ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-heritage-blue mx-auto"></div>
                <p className="text-sm text-gray-600 mt-2">Loading templates...</p>
              </div>
            ) : availableTemplates.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Feedback Template</Label>
                  <Badge variant="secondary">{availableTemplates.length} available</Badge>
                </div>
                
                <Select
                  value={selectedTemplate?.id || ''}
                  onValueChange={handleTemplateChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex items-center gap-2">
                          <span>{template.template_name}</span>
                          {template.is_default && <Badge variant="default" className="text-xs">Default</Badge>}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {selectedTemplate && (
                  <div className="text-sm">
                    <p className="text-gray-600">{selectedTemplate.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>📝 {selectedTemplate.template_questions?.length || 0} questions</span>
                      {selectedTemplate.estimated_completion_time && (
                        <span>⏱️ ~{selectedTemplate.estimated_completion_time} min</span>
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-amber-700 font-medium">⚠️ No templates configured</p>
                <p className="text-xs text-gray-600 mt-1">
                  Using default questions. Ask your admin to create templates in Question Bank.
                </p>
              </div>
            )}
          </div>

          {/* Questions — shown as stakeholders will see them: under a heading
              per category, the requester's own questions last. */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">
              Feedback Questions ({questions.length})
            </Label>
            <p className="text-sm text-gray-600">
              {useTemplate && selectedTemplate
                ? `Starts from the "${selectedTemplate.template_name}" template. Change, remove or add questions: stakeholders answer this list. A template question you reword becomes one of your own.`
                : 'These questions will be sent to selected stakeholders. Change, remove or add questions.'}
            </p>

            <div className="space-y-4">
              {(() => {
                const sections = questionSections(questions)
                const shown = sections.flatMap((s) => s.questions)
                return sections.map((section) => (
                  <div key={section.key} className="space-y-2">
                    {section.title && (
                      <p className="text-xs font-semibold uppercase tracking-wide text-heritage-blue">
                        {section.title}
                      </p>
                    )}
                    {section.questions.map((question) => {
                      const index = questions.indexOf(question)
                      const number = shown.indexOf(question) + 1
                      const editing = editingIndex === index
                      return (
                        <div key={question.id} className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-500 mt-0.5">{number}.</span>
                          {editing ? (
                            <div className="flex flex-1 items-center gap-2">
                              <Input
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault()
                                    saveEditing()
                                  }
                                }}
                                aria-label={`Question ${number}`}
                                autoFocus
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={saveEditing}
                                disabled={!editingText.trim()}
                                aria-label="Save question"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditingIndex(null)}
                                aria-label="Keep the question as it was"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <div className="flex-1">
                                <p className="text-sm">{question.text}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {answerTypeLabel(question)}
                                  {!question.required && ' · optional'}
                                </p>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => startEditing(index)}
                                className="h-6 w-6 p-0"
                                aria-label={`Change question ${number}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeQuestion(index)}
                                className="h-6 w-6 p-0"
                                aria-label={`Remove question ${number}`}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                ))
              })()}
            </div>

            <div className="flex gap-2">
              <Input
                placeholder="Add a custom question..."
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCustomQuestion()
                  }
                }}
              />
              <Button type="button" onClick={addCustomQuestion} size="sm">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Recommended due date
            </Label>
            <p className="text-xs text-gray-500">
              A gentle suggestion for when you'd appreciate a response — not a hard deadline.
            </p>
            <Input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              min={sastToday()}
            />
          </div>

          {/* Options */}
          <div className="space-y-3 border-t pt-4">
            <Label className="text-base font-semibold">Options</Label>
            
            <div className="flex items-start gap-3">
              <Checkbox
                checked={isConfidential}
                onCheckedChange={(checked) => setIsConfidential(checked === true)}
                id="confidential"
              />
              <div className="flex-1">
                <label htmlFor="confidential" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Confidential Responses
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Feedback provider names will be hidden from responses.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                checked={enableAI}
                onCheckedChange={(checked) => setEnableAI(checked === true)}
                id="ai-analysis"
              />
              <div className="flex-1">
                <label htmlFor="ai-analysis" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Enable AI Analysis
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  Generate insights and themes from feedback responses
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sticky, so the send button is in view however long the question
            list is; the hint says why it is not available yet. */}
        <DialogFooter className="sticky bottom-0 z-10 -mx-4 -mb-4 border-t bg-white px-4 py-3 sm:-mx-6 sm:-mb-6 sm:items-center sm:px-6">
          {noStakeholders && (
            <p className="text-xs text-gray-600 sm:mr-auto">
              Choose at least one stakeholder above to send this request.
            </p>
          )}
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || noStakeholders}
            className="bg-heritage-blue hover:bg-heritage-blue-dark"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Send Feedback Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
